import clsx from 'clsx';
import {
  formatDeviation,
  formatContextList,
  formatViolationActual,
  formatWpsLimitRange,
  wpsDirectionLabel,
  type GroupedViolation,
} from '../../../utils/wpsViolationModel';

interface WpsParameterComparisonProps {
  group: GroupedViolation;
  className?: string;
  hideContextLine?: boolean;
}

function ComparisonColumn({
  label,
  value,
  valueClassName,
}: {
  label: string;
  value: string;
  valueClassName?: string;
}) {
  return (
    <div className="min-w-0 text-center">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-heritage-grey">{label}</p>
      <p
        className={clsx(
          'mt-1 truncate font-heading text-2xl font-bold tabular-nums sm:text-3xl',
          valueClassName ?? 'text-blueprint-blue',
        )}
      >
        {value}
      </p>
    </div>
  );
}

export const WpsParameterComparison = ({
  group,
  className,
  hideContextLine = false,
}: WpsParameterComparisonProps) => {
  const deviationLabel = formatDeviation({
    parameterKey: group.parameterKey,
    parameterLabel: group.parameterLabel,
    actual: group.actual,
    min: group.min,
    max: group.max,
    deviationAbs: null,
    deviationPct: group.worstDeviationPct,
    direction: group.direction,
    level: 'pass',
  });
  const hasActualSpread =
    group.actualMin != null
    && group.actualMax != null
    && group.actualMin !== group.actualMax;
  const contextLine = formatContextList(group.contexts);

  return (
    <div className={clsx('border-b border-[#EEF0F3] py-4 last:border-0', className)}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h5 className="font-heading text-sm font-semibold text-blueprint-blue">{group.parameterLabel}</h5>
        <span className="rounded-full border border-hero-red-20 bg-hero-red-10 px-2 py-0.5 text-[10px] font-semibold text-readable-red">
          {wpsDirectionLabel(group.direction)}
        </span>
      </div>

      <div className="rounded-xl border border-[#E2E5EA] bg-page-bg p-4">
        <div className="grid grid-cols-3 gap-2">
          <ComparisonColumn
            label="Actual"
            value={formatViolationActual(group.actual, group.parameterKey)}
          />
          <ComparisonColumn
            label="Deviation"
            value={deviationLabel}
            valueClassName="text-readable-red"
          />
          <ComparisonColumn
            label="WPS limit"
            value={formatWpsLimitRange(group.min, group.max)}
          />
        </div>

        {hasActualSpread && (
          <p className="mt-2 text-center text-[11px] text-heritage-grey">
            Across locations:{' '}
            {formatViolationActual(group.actualMin, group.parameterKey)}
            {' – '}
            {formatViolationActual(group.actualMax, group.parameterKey)}
          </p>
        )}
      </div>

      {!hideContextLine && contextLine && (
        <p className="mt-2 text-xs text-heritage-grey">{contextLine}</p>
      )}

      <p className="mt-1.5 text-sm leading-snug text-text-grey">{group.hint}</p>
    </div>
  );
};
